'use client';

import { useEffect } from 'react';

import { initAmplitudeSessionReplay } from '@/utils/amplitudeSessionReplay';

/**
 * Validates that the sample rate is a valid number greater than 0 and less than or equal to 1
 */
function isValidSampleRate(sampleRate: number): boolean {
  return !isNaN(sampleRate) && sampleRate > 0 && sampleRate <= 1;
}

/**
 * AmplitudeSessionReplay component
 *
 * Initializes Amplitude Session Replay when the component mounts.
 * This component should be included once in the application layout.
 *
 * Configuration is controlled via environment variables:
 * - NEXT_PUBLIC_AMPLITUDE_API_KEY: Your Amplitude API key
 * - NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE: Sample rate (0-1, default: 0)
 *
 * @example
 * ```tsx
 * <AmplitudeSessionReplay />
 * ```
 */
export function AmplitudeSessionReplay() {
  useEffect(() => {
    // Don't initialize if API key is missing or sample rate is invalid
    const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
    const sampleRate = parseFloat(
      process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE || '0',
    );

    if (!apiKey || !isValidSampleRate(sampleRate)) {
      return;
    }

    let retryCount = 0;
    const maxRetries = 5; // Try for 5 seconds (5 × 1000ms)
    let timeoutId: NodeJS.Timeout | null = null;

    // Wait for RudderStack to be ready to ensure we have access to device IDs and session management
    const checkRudderstackAndInit = () => {
      if (typeof window !== 'undefined' && window.rudderanalytics) {
        initAmplitudeSessionReplay(apiKey, sampleRate);
      } else if (retryCount < maxRetries) {
        retryCount++;
        timeoutId = setTimeout(checkRudderstackAndInit, 1000);
      }
    };

    checkRudderstackAndInit();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return null;
}

export default AmplitudeSessionReplay;
